
import { PythonModule } from '../types';

export const PYTHON_REGEX_MODULE: PythonModule = {
  id: "python-regex",
  title: "Regular Expressions for Text Processing",
  description: "Master regular expressions for powerful text processing, pattern matching, and data extraction.",
  difficulty: "intermediate",
  content: `
# Regular Expressions for Text Processing

Regular expressions (regex) are powerful tools for pattern matching, text processing, and data extraction.

## Regex Basics

### Simple Pattern Matching

\`\`\`python
import re

text = "Hello, my phone number is 555-123-4567"

# Basic search
if re.search(r"phone", text):
    print("Found 'phone' in text")

# Find phone number pattern
phone_pattern = r"\\d{3}-\\d{3}-\\d{4}"
match = re.search(phone_pattern, text)
if match:
    print(f"Phone number found: {match.group()}")
\`\`\`

### Common Regex Patterns

\`\`\`python
# Character classes
patterns = {
    r"\\d": "Any digit (0-9)",
    r"\\w": "Any word character (a-z, A-Z, 0-9, _)",
    r"\\s": "Any whitespace character",
    r"\\D": "Any non-digit",
    r"\\W": "Any non-word character",
    r"\\S": "Any non-whitespace character",
    r".": "Any character except newline",
    r"[a-z]": "Any lowercase letter",
    r"[A-Z]": "Any uppercase letter",
    r"[0-9]": "Any digit",
    r"[aeiou]": "Any vowel"
}

# Quantifiers
quantifiers = {
    r"*": "Zero or more",
    r"+": "One or more",
    r"?": "Zero or one",
    r"{3}": "Exactly 3",
    r"{3,5}": "Between 3 and 5",
    r"{3,}": "3 or more"
}
\`\`\`

## Regex Functions

### re.search() vs re.match() vs re.findall()

\`\`\`python
text = "The price is $25.99 and tax is $3.50"

# re.search() - finds first occurrence anywhere in string
match = re.search(r"\\$\\d+\\.\\d{2}", text)
if match:
    print(f"First price found: {match.group()}")  # $25.99

# re.match() - matches only at the beginning
match = re.match(r"The", text)
if match:
    print(f"Text starts with: {match.group()}")  # The

# re.findall() - finds all occurrences
prices = re.findall(r"\\$\\d+\\.\\d{2}", text)
print(f"All prices: {prices}")  # ['$25.99', '$3.50']

# re.finditer() - returns match objects for all occurrences
for match in re.finditer(r"\\$\\d+\\.\\d{2}", text):
    print(f"Price at position {match.start()}-{match.end()}: {match.group()}")
\`\`\`

## Practical Applications

### Email Validation

\`\`\`python
def validate_email(email):
    pattern = r"^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$"
    return re.match(pattern, email) is not None

# Test emails
emails = [
    "user@example.com",      # Valid
    "test.email@domain.co.uk",  # Valid
    "invalid.email",         # Invalid
    "@domain.com",          # Invalid
    "user@.com"             # Invalid
]

for email in emails:
    print(f"{email}: {'Valid' if validate_email(email) else 'Invalid'}")
\`\`\`

### Data Cleaning and Extraction

\`\`\`python
def clean_and_extract_data(messy_data):
    # Remove extra whitespace
    cleaned = re.sub(r"\\s+", " ", messy_data).strip()
    
    # Extract structured data
    patterns = {
        'names': r"Name:\\s*([A-Za-z\\s]+)",
        'ages': r"Age:\\s*(\\d+)",
        'emails': r"Email:\\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,})",
        'phones': r"Phone:\\s*([\\d\\-\\(\\)\\s]+)"
    }
    
    extracted_data = {}
    for key, pattern in patterns.items():
        matches = re.findall(pattern, cleaned, re.IGNORECASE)
        extracted_data[key] = matches
    
    return extracted_data
\`\`\`

## Best Practices

1. **Compile patterns** for repeated use with re.compile()
2. **Use raw strings** (r"pattern") to avoid escaping issues
3. **Be specific** rather than overly broad in patterns
4. **Test patterns thoroughly** with various inputs
5. **Consider performance** for large text processing tasks

## Practice Exercise

Create a log file analyzer that extracts timestamps, log levels, and error messages using regular expressions.
`,
  duration: "60 minutes",
  objectives: [
    "Understand regular expression syntax and patterns",
    "Master Python's re module functions",
    "Apply regex for data validation and extraction",
    "Optimize regex patterns for performance",
    "Build practical text processing tools"
  ],
  order: 6
};
